import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  sendChatMessage as sendChatMessageThunk,
  initiateOnboarding,
  verifyOnboardingPhone,
  resendOtpAction,
  getOnboardingSession,
  getPmsConnectionStatus,
} from '../redux/onboarding/actionCreator';
import { getItem } from '../../utility/localStorageControl';
import useHubSpot from '../hooks/useHubSpot';
import { initLogQueue, setLoggerContext, logEvent, LOG_EVENT_TYPE, LOG_STATUS } from '../utils/logger';

export const STEP_TO_PATH = {
  'qualify':                          '/onboarding/OB2',
  'demo-tour':                        '/onboarding/OB3',
  'lets-go':                          '/onboarding/OB4',
  'connect-pms':                      '/onboarding/OB5',
  'plan':                             '/onboarding/OB6',
  'guest-registration':               '/onboarding/OB7',
  'customize':                        '/onboarding/OB8',
  'sign':                             '/onboarding/OB9',
  'id-verify':                        '/onboarding/OB10',
  'damage':                           '/onboarding/OB11',
  'coverage':                         '/onboarding/OB12',
  'social-proof':                     '/onboarding/OB13',
  'proof-stats':                      '/onboarding/OB13B',
  'booking-apply':                    '/onboarding/OB14',
  'guidebooks-reveal':                '/onboarding/OB15',
  'arrival-choice':                   '/onboarding/OB16',
  'arrival-confirmation':             '/onboarding/OB17',
  'guidebook':                        '/onboarding/OB18',
  'guest-registration-overview':      '/onboarding/OB19',
  'guest-registration-confirmation':  '/onboarding/OB20',
  'task-intro':                       '/onboarding/OB21',
  'task-detail':                      '/onboarding/OB22',
  'messaging-intro':                  '/onboarding/OB23',
  'messaging-templates':              '/onboarding/OB24',
  'sms-reveal':                       '/onboarding/OB25',
  'disable-pms-messages':             '/onboarding/OB26',
  'complete':                         '/onboarding/OB27',
};

export const PATH_TO_STEP = Object.fromEntries(
  Object.entries(STEP_TO_PATH).map(([slug, path]) => [path, slug])
);

const POLL_INTERVAL_MS = 5000; // 5 seconds between status checks
const POLL_MAX         = 60;   // ~5 minutes maximum polling window

/**
 * OnboardingContext
 * Shared state for the entire onboarding flow (screens 1–22).
 * Persists across screen navigation via the provider wrapping all routes.
 *
 * State shape matches master doc spec exactly.
 */

const initialState = {
  sessionSlug: null,
  currentStepSlug: 'welcome',
  completedSteps: [],
  hostName: null,
  hostEmail: null,
  hostPhone: null,
  jwtToken: null,
  isVerified: false,
  verifiedVia: null, // 'popup' | 'chatbot' | null
  listings: [],
  pmsProviderHint: null,
  qualificationData: null,   // { properties_count, pms_used, current_protections, looking_for, heard_about_us }
  stripeClientSecret: null,  // client_secret returned by POST /stripe/setup-intent; consumed by StripeCardModal
  connectedPms: null, // { slug, name } when onboarding PMS is connected; null otherwise
  pmsList: [], // cached PMS provider list (fetchPmsList result) — avoids re-fetch on back-navigation
  themesList: [], // cached themes list (getOnboardingThemes result) — avoids re-fetch on back-navigation
  messageTemplates: [], // cached message templates (fetchMessageTemplates result) — avoids re-fetch on back-navigation
  taskVerbiages: null, // cached task verbiages (fetchTasks + fetchTaskVerbiages result) — prefetched in TaskIntroStep
  pmsHelpText: null, // cached PMS credential help text (fetchCmsContent action_type:23) — avoids re-fetch on back-navigation
  hasActivePms: null, // from booking-apply step response — null until step is saved
  stripePaymentCaptured: false, // true after confirmStripePaymentMethod succeeds (D4 unlocked)
  chatHistory: [], // [{ role: 'bot'|'user', message: string }]
  isChatLoading: false,
  chatAuthCode: null,
  sessionLoaded: false, // true once getOnboardingSession resolves (success or failure)
};

// ---------------------------------------------------------------------------
// Action types
// ---------------------------------------------------------------------------

const CTX_ACTIONS = {
  SET_SESSION: 'SET_SESSION',
  SET_VERIFIED: 'SET_VERIFIED',
  SET_STEP: 'SET_STEP',
  COMPLETE_STEP: 'COMPLETE_STEP',
  SET_LISTINGS: 'SET_LISTINGS',
  SET_PMS_HINT: 'SET_PMS_HINT',
  APPEND_CHAT: 'APPEND_CHAT',
  RESET: 'RESET',
  SET_CHAT_LOADING: 'SET_CHAT_LOADING',
  SET_CHAT_AUTH_CODE: 'SET_CHAT_AUTH_CODE',
  SET_QUALIFICATION: 'SET_QUALIFICATION',
  SET_STRIPE_CLIENT_SECRET: 'SET_STRIPE_CLIENT_SECRET',
  SET_STRIPE_PAYMENT_CAPTURED: 'SET_STRIPE_PAYMENT_CAPTURED',
  SET_CONNECTED_PMS: 'SET_CONNECTED_PMS',
  SET_PMS_LIST: 'SET_PMS_LIST',
  SET_THEMES_LIST: 'SET_THEMES_LIST',
  SET_MESSAGE_TEMPLATES: 'SET_MESSAGE_TEMPLATES',
  SET_TASK_VERBIAGES: 'SET_TASK_VERBIAGES',
  SET_PMS_HELP_TEXT: 'SET_PMS_HELP_TEXT',
  SET_SESSION_LOADED: 'SET_SESSION_LOADED',
  SET_HAS_ACTIVE_PMS: 'SET_HAS_ACTIVE_PMS',
};

// Reducer

const onboardingReducer = (state, action) => {
  switch (action.type) {
    case CTX_ACTIONS.SET_SESSION:
      return {
        ...state,
        sessionSlug: action.payload.sessionSlug,
        currentStepSlug: action.payload.currentStepSlug ?? state.currentStepSlug,
        completedSteps: action.payload.completedSteps ?? state.completedSteps,
      };

    case CTX_ACTIONS.SET_VERIFIED:
      return {
        ...state,
        isVerified: true,
        verifiedVia: action.payload.verifiedVia,
        jwtToken: action.payload.jwtToken,
        hostName: action.payload.hostName ?? state.hostName,
        hostEmail: action.payload.hostEmail ?? state.hostEmail,
        hostPhone: action.payload.hostPhone ?? state.hostPhone,
        sessionSlug: action.payload.sessionSlug ?? state.sessionSlug,
      };

    case CTX_ACTIONS.SET_STEP:
      return { ...state, currentStepSlug: action.payload };

    case CTX_ACTIONS.COMPLETE_STEP:
      return {
        ...state,
        completedSteps: state.completedSteps.includes(action.payload)
          ? state.completedSteps
          : [...state.completedSteps, action.payload],
      };

    case CTX_ACTIONS.SET_LISTINGS:
      return { ...state, listings: action.payload };

    case CTX_ACTIONS.SET_PMS_LIST:
      return { ...state, pmsList: action.payload };

    case CTX_ACTIONS.SET_THEMES_LIST:
      return { ...state, themesList: action.payload };

    case CTX_ACTIONS.SET_MESSAGE_TEMPLATES:
      return { ...state, messageTemplates: action.payload };

    case CTX_ACTIONS.SET_TASK_VERBIAGES:
      return { ...state, taskVerbiages: action.payload };

    case CTX_ACTIONS.SET_PMS_HELP_TEXT:
      return { ...state, pmsHelpText: action.payload };

    case CTX_ACTIONS.SET_PMS_HINT:
      return { ...state, pmsProviderHint: action.payload };

    case CTX_ACTIONS.APPEND_CHAT:
      return {
        ...state,
        chatHistory: [...state.chatHistory, action.payload],
      };

    case CTX_ACTIONS.RESET:
      return { ...initialState };

    case CTX_ACTIONS.SET_CHAT_LOADING:
      return { ...state, isChatLoading: action.payload };

    case CTX_ACTIONS.SET_CHAT_AUTH_CODE:
      return { ...state, chatAuthCode: action.payload };

    case CTX_ACTIONS.SET_QUALIFICATION:
      return { ...state, qualificationData: action.payload };

    case CTX_ACTIONS.SET_STRIPE_CLIENT_SECRET:
      return { ...state, stripeClientSecret: action.payload };

    case CTX_ACTIONS.SET_STRIPE_PAYMENT_CAPTURED:
      // payload may be an explicit boolean (session hydration) or absent (post-confirm call).
      return { ...state, stripePaymentCaptured: action.payload ?? true };

    case CTX_ACTIONS.SET_CONNECTED_PMS:
      return { ...state, connectedPms: action.payload };

    case CTX_ACTIONS.SET_HAS_ACTIVE_PMS:
      return { ...state, hasActivePms: action.payload };

    case CTX_ACTIONS.SET_SESSION_LOADED:
      return { ...state, sessionLoaded: true };

    default:
      return state;
  }
};

// Context + Provider

const OnboardingContext = createContext(null);

const CHAT_STORAGE_KEY = 'hg_onboarding_chat_history';

const hydrateInitialState = (init) => {
  try {
    const saved = localStorage.getItem(CHAT_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : null;
    if (Array.isArray(parsed) && parsed.length > 0) {
      return { ...init, chatHistory: parsed };
    }
  } catch {}
  return init;
};

export const OnboardingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(onboardingReducer, initialState, hydrateInitialState);
  const reduxDispatch = useDispatch();
  const navigate        = useNavigate();
  const location        = useLocation();
  const { trackStep, identify } = useHubSpot();

  // Refs for background PMS validation polling — survive step navigation.
  const pollIntervalRef = useRef(null);
  const pollCountRef    = useRef(0);

  // Persist chatHistory to localStorage on every change so it survives page refresh.
  useEffect(() => {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(state.chatHistory));
  }, [state.chatHistory]);

  // Latest-ref pattern: gives appendChat access to the current chatHistory
  // without adding it to the dependency array (which would change appendChat's
  // reference on every message and break greeting-fired guards in step files).
  const chatHistoryRef = useRef(state.chatHistory);
  useEffect(() => { chatHistoryRef.current = state.chatHistory; }, [state.chatHistory]);

  // Journey logging (see public/ONBOARDING_LOGGING_PLAN.md).
  // Start the background log queue once — it sets up its own flush timers
  // and listeners internally and is a no-op on any later remount.
  // This is additive/observational only: it never reads from or writes to
  // any of the onboarding state above, so it cannot change existing flow.
  useEffect(() => {
    initLogQueue(reduxDispatch);
  }, [reduxDispatch]);

  // Keep the log queue's session identity (sessionSlug/hostEmail) in sync
  // with context state, so every log batch is tagged correctly even as the
  // host moves from anonymous → verified → session-restored.
  useEffect(() => {
    setLoggerContext({ sessionSlug: state.sessionSlug, hostEmail: state.hostEmail });
  }, [state.sessionSlug, state.hostEmail]);

  // Hydrate context from the backend session on mount.
  // Covers the page-refresh case where context state resets mid-flow.
  // Restores: session progress, host identity (name → auto-fills CustomizeStep),
  // and Stripe payment state. Only runs when a JWT cookie is present.
  useEffect(() => {
    const token = getItem('onboarding_access_token');
    if (!token) return;
    reduxDispatch(getOnboardingSession())
      .then((result) => {
        if (!result) return;
        // Restore session progress (current step + completed steps)
        if (result.session_slug || result.current_step) {
          dispatch({
            type: CTX_ACTIONS.SET_SESSION,
            payload: {
              sessionSlug:     result.session_slug   ?? null,
              currentStepSlug: result.current_step   ?? undefined,
              completedSteps:  result.completed_steps ?? [],
            },
          });
        }
        // Restore host identity so name pre-fills on CustomizeStep after refresh
        if (result.host_name) {
          dispatch({
            type: CTX_ACTIONS.SET_VERIFIED,
            payload: {
              jwtToken:    null,
              verifiedVia: result.verified_via ?? null,
              hostName:    result.host_name,
              hostEmail:   result.host_email  ?? null,
              hostPhone:   result.host_phone  ?? null,
              sessionSlug: result.session_slug ?? null,
            },
          });
        }
        // HubSpot: link the resumed session to the host's contact record.
        identify(result.host_email);
        // Restore Stripe payment state
        if (result.has_payment_method) {
          dispatch({ type: CTX_ACTIONS.SET_STRIPE_PAYMENT_CAPTURED, payload: true });
        }
        // Restore connected PMS (Phase 5)
        dispatch({ type: CTX_ACTIONS.SET_CONNECTED_PMS, payload: result.connected_pms ?? null });
        // Restore has_active_pms if available in session
        if (result.has_active_pms !== undefined) {
          dispatch({ type: CTX_ACTIONS.SET_HAS_ACTIVE_PMS, payload: result.has_active_pms });
        }
        dispatch({ type: CTX_ACTIONS.SET_SESSION_LOADED });
      })
      .catch(() => {
        // Session fetch is best-effort — silent failure is acceptable.
        dispatch({ type: CTX_ACTIONS.SET_SESSION_LOADED });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set session data after initiate/verify
  const setSession = useCallback((sessionSlug, currentStepSlug, completedSteps) => {
    dispatch({ type: CTX_ACTIONS.SET_SESSION, payload: { sessionSlug, currentStepSlug, completedSteps } });
  }, []);

  // Mark host as verified (after OTP success)
  const setVerified = useCallback(({ jwtToken, verifiedVia, hostName, hostEmail, hostPhone, sessionSlug }) => {
    dispatch({ type: CTX_ACTIONS.SET_VERIFIED, payload: { jwtToken, verifiedVia, hostName, hostEmail, hostPhone, sessionSlug } });
    // HubSpot: link this browser session to the host's contact record after OTP verify.
    identify(hostEmail);
  }, [identify]);

  // Advance current step
  const setCurrentStep = useCallback((stepSlug) => {
    dispatch({ type: CTX_ACTIONS.SET_STEP, payload: stepSlug });
  }, []);

  // Mark a step as completed.
  // extra: optional step-specific fields to forward to HubSpot
  // (e.g. { coverageTier } from CoverageTiersStep, { idVerificationType } from IdVerify).
  const completeStep = useCallback((stepSlug, extra = {}) => {
    dispatch({ type: CTX_ACTIONS.COMPLETE_STEP, payload: stepSlug });

    // Note: no generic NAVIGATION log here on purpose. Static screens only
    // need their one SCREEN_VIEW (the next screen's own SCREEN_VIEW already
    // proves the host moved on), and the three complex screens already log
    // their own BUTTON_CLICK/PROCESS_* events for the action that led here —
    // a blanket NAVIGATION on every completeStep call would just be a
    // redundant third log for those.

    // HubSpot: fire after every step save success.
    const furthestStep = state.completedSteps.includes(stepSlug)
      ? state.completedSteps.length
      : state.completedSteps.length + 1;
    trackStep({
      email:              state.hostEmail,
      currentStep:        stepSlug,
      furthestStep,
      lastStepAt:         new Date().toISOString(),
      pmsConnected:       state.connectedPms?.name || null,
      numberOfProperties: state.qualificationData?.properties_count ?? null,
      ...extra,
    });
  }, [state.completedSteps, state.hostEmail, state.connectedPms, state.qualificationData, trackStep]);

  // Set synced listings (after PMS connect)
  const setListings = useCallback((listings) => {
    dispatch({ type: CTX_ACTIONS.SET_LISTINGS, payload: listings });
  }, []);

  // Store PMS provider hint captured by chatbot
  const setPmsHint = useCallback((hint) => {
    dispatch({ type: CTX_ACTIONS.SET_PMS_HINT, payload: hint });
  }, []);

  // Append a message to chat history.
  // Blank messages are dropped: the API's chat_history validation requires
  // a non-empty `message` on every entry, so a blank greeting here would
  // break every later chat request in the session.
  // Duplicate-check via chatHistoryRef prevents step greeting messages from
  // re-firing when the user navigates back and the component remounts.
  const appendChat = useCallback((role, message) => {
    if (!message || !message.trim()) return;
    // Deduplicate bot messages only — users can legitimately send the same text multiple times.
    if (role === 'bot' && chatHistoryRef.current.some((m) => m.role === 'bot' && m.message === message)) return;
    dispatch({ type: CTX_ACTIONS.APPEND_CHAT, payload: { role, message } });
  }, []);

  /**
   * sendChatMessage
   * Appends the user message immediately, calls the RAG chatbot API, appends
   * the bot reply, then dispatches follow-up actions based on the structured intent.
   *
   * Actions handled:
   *   trigger_initiate    → call initiateOnboarding with extracted name+email,
   *                         store the returned auth_code in context state.
   *   trigger_verify      → call verifyOnboardingEmail with the stored auth_code
   *                         and extracted OTP; on success update verified state.
   *   trigger_resend_otp  → call resendOtpAction, refresh the stored auth_code.
   *   capture_pms_hint    → store the PMS provider name in context.
   *   none                → no side-effect beyond displaying the bot reply.
   */
  const sendChatMessage = useCallback(
    async (message) => {
      // Always derive step_slug from the current URL so it reflects where the
      // user actually is — context state can lag when navigating back/forward.
      const routeMatch = location.pathname.match(/\/onboarding\/([^/]+)/);
      const obCode = routeMatch ? routeMatch[1] : null;
      const stepSlug = obCode ? (PATH_TO_STEP[`/onboarding/${obCode}`] ?? state.currentStepSlug) : state.currentStepSlug;

      // Append user turn immediately so the UI feels responsive.
      appendChat('user', message);
      dispatch({ type: CTX_ACTIONS.SET_CHAT_LOADING, payload: true });

      try {
        // state.chatHistory is the history *before* the user turn just appended
        // (React batches the state update; the closed-over value is still the
        // prior snapshot). This is intentional — the API receives prior turns in
        // chat_history and the current message in message.
        const result = await reduxDispatch(
          sendChatMessageThunk(message, stepSlug, state.sessionSlug, state.chatHistory),
        );

        if (!result) {
          appendChat('bot', "I'm having trouble connecting right now. Please try again.");
          return;
        }

        const { message: botMessage, action, data = {} } = result;

        if (botMessage) {
          appendChat('bot', botMessage);
        }

        switch (action) {
          case 'trigger_initiate': {
            // Bot detected name + email — trigger account initiation and capture auth_code.
            const initiateResult = await reduxDispatch(initiateOnboarding(data.name, data.email));
            if (initiateResult?.result?.auth_code) {
              dispatch({ type: CTX_ACTIONS.SET_CHAT_AUTH_CODE, payload: initiateResult.result.auth_code });
            }
            break;
          }
          case 'trigger_verify': {
            // Bot detected a 6-digit OTP — verify against the stored auth_code.
            const verifyResult = await reduxDispatch(
              verifyOnboardingPhone(state.chatAuthCode, data.otp_code),
            );
            if (verifyResult?.result) {
              const r = verifyResult.result;
              setVerified({
                jwtToken: r.jwt_token,
                verifiedVia: 'sms',
                hostName: r.host_name,
                hostEmail: r.host_email,
                hostPhone: r.host_phone,
                sessionSlug: r.session_slug,
              });
            }
            break;
          }
          case 'trigger_resend_otp': {
            // Host asked for a new code — resend and refresh the stored auth_code.
            const resendResult = await reduxDispatch(resendOtpAction(state.chatAuthCode));
            if (resendResult?.result?.auth_code) {
              dispatch({ type: CTX_ACTIONS.SET_CHAT_AUTH_CODE, payload: resendResult.result.auth_code });
            }
            break;
          }
          case 'capture_pms_hint': {
            if (data.pms_name) {
              setPmsHint(data.pms_name);
            }
            break;
          }
          default:
            break;
        }
      } catch (_err) {
        appendChat('bot', "I'm having trouble connecting right now. Please try again.");
      } finally {
        dispatch({ type: CTX_ACTIONS.SET_CHAT_LOADING, payload: false });
      }
    },
    // state.currentStepSlug, state.sessionSlug, state.chatHistory, state.chatAuthCode
    // are included because the callback must read the latest values at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appendChat, reduxDispatch, location.pathname, state.currentStepSlug, state.sessionSlug, state.chatHistory, state.chatAuthCode, setVerified, setPmsHint],
  );

  // Store qualification answers after Screen 3 submit
  const setQualificationData = useCallback((data) => {
    dispatch({ type: CTX_ACTIONS.SET_QUALIFICATION, payload: data });
  }, []);

  // Store the Stripe SetupIntent client_secret for StripeCardModal to consume
  const setStripeClientSecret = useCallback((secret) => {
    dispatch({ type: CTX_ACTIONS.SET_STRIPE_CLIENT_SECRET, payload: secret });
  }, []);

  // Mark Stripe card capture as complete (D4 AI queue unlocked).
  // Accepts an optional boolean so session hydration can pass the server-side value.
  const setStripePaymentCaptured = useCallback((value = true) => {
    dispatch({ type: CTX_ACTIONS.SET_STRIPE_PAYMENT_CAPTURED, payload: value });
  }, []);

  // Stop any running PMS validation poll.
  const stopPmsPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Start background polling for PMS credential validation result.
  // Call this from PmsCredentialPopup immediately after the optimistic 200 advance.
  // Polls GET /onboarding/pms-connection-status every 5 s (max ~5 min).
  //   active → stop silently (credentials accepted).
  //   error  → stop, clear connectedPms, navigate back with the rejection reason.
  //
  // Journey logging note: this poll lives here (context-level, via refs) rather
  // than inside ConnectPmsStep specifically because it must keep running and keep
  // logging correctly even if the host has already navigated to Plan or Coverage
  // by the time it resolves — that's the whole point of it being background.
  // startedAt (defaults to "now" if the caller doesn't pass one) is what lets us
  // log how long the connection attempt actually took once it resolves.
  const startPmsPolling = useCallback((provider, startedAt = Date.now()) => {
    stopPmsPolling(); // clear any previous interval
    pollCountRef.current = 0;

    logEvent('connect-pms', LOG_EVENT_TYPE.PROCESS_STARTED, {
      action: 'PMS_CONNECTION_POLL',
      status: LOG_STATUS.STARTED,
      metadata: { pms: provider?.name ?? null },
    });

    pollIntervalRef.current = setInterval(async () => {
      pollCountRef.current += 1;

      if (pollCountRef.current >= POLL_MAX) {
        stopPmsPolling();
        logEvent('connect-pms', LOG_EVENT_TYPE.PROCESS_FAILED, {
          action: 'PMS_CONNECTION_POLL',
          status: LOG_STATUS.FAILED,
          errorCode: 'PMS_POLL_TIMEOUT',
          errorMessage: 'PMS connection did not resolve within the polling window.',
          metadata: { pms: provider?.name ?? null, durationMs: Date.now() - startedAt },
        });
        return;
      }

      try {
        const data   = await reduxDispatch(getPmsConnectionStatus());
        const status = data?.result?.connection_status;

        if (status === 'active') {
          stopPmsPolling();
          logEvent('connect-pms', LOG_EVENT_TYPE.PROCESS_COMPLETED, {
            action: 'PMS_CONNECTION_POLL',
            status: LOG_STATUS.SUCCESS,
            metadata: { pms: provider?.name ?? null, durationMs: Date.now() - startedAt },
          });
          // Set connectedPms only on confirmed connection — not optimistically on submit.
          if (provider) {
            dispatch({ type: CTX_ACTIONS.SET_CONNECTED_PMS, payload: { slug: provider.slug, name: provider.name } });
          }
        } else if (status === 'error') {
          logEvent('connect-pms', LOG_EVENT_TYPE.PROCESS_FAILED, {
            action: 'PMS_CONNECTION_POLL',
            status: LOG_STATUS.FAILED,
            errorMessage: data?.result?.error_message ?? null,
            metadata: { pms: provider?.name ?? null, durationMs: Date.now() - startedAt },
          });
          stopPmsPolling();
          const errorMsg = data?.result?.error_message
            || 'PMS connection failed. Please check your credentials and try again.';
          // Clear the optimistically-set connectedPms so ConnectPmsStep renders
          // the selection grid (not the "Connected" view) when the user lands back.
          dispatch({ type: CTX_ACTIONS.SET_CONNECTED_PMS, payload: null });
          navigate(STEP_TO_PATH['connect-pms'], {
            replace: true,
            state:   { pmsError: errorMsg },
          });
        }
      } catch {
        // Swallow poll errors — keep polling until POLL_MAX
      }
    }, POLL_INTERVAL_MS);
  }, [reduxDispatch, stopPmsPolling, navigate, dispatch]);

  // Update connected PMS state (Phase 5).
  // payload: { slug, name } or null.
  const setConnectedPms = useCallback((pms) => {
    dispatch({ type: CTX_ACTIONS.SET_CONNECTED_PMS, payload: pms });
  }, []);

  const setHasActivePms = useCallback((value) => {
    dispatch({ type: CTX_ACTIONS.SET_HAS_ACTIVE_PMS, payload: value });
  }, []);

  // Cache the PMS provider list — avoids re-fetching fetchPmsList() on
  // back-navigation to ConnectPmsStep.
  const setPmsList = useCallback((list) => {
    dispatch({ type: CTX_ACTIONS.SET_PMS_LIST, payload: list });
  }, []);

  // Cache the themes list — avoids re-fetching getOnboardingThemes() on
  // back-navigation to CustomizeStep and ReviewGuidebookStep.
  const setThemesList = useCallback((list) => {
    dispatch({ type: CTX_ACTIONS.SET_THEMES_LIST, payload: list });
  }, []);

  // Cache the message templates — avoids re-fetching fetchMessageTemplates() on
  // back-navigation to MessageTemplatesStep.
  const setMessageTemplates = useCallback((list) => {
    dispatch({ type: CTX_ACTIONS.SET_MESSAGE_TEMPLATES, payload: list });
  }, []);

  // Cache task verbiages — prefetched in TaskIntroStep so TaskDetailStep shows instantly.
  const setTaskVerbiages = useCallback((list) => {
    dispatch({ type: CTX_ACTIONS.SET_TASK_VERBIAGES, payload: list });
  }, []);

  // Cache the PMS credential help text — avoids re-fetching fetchCmsContent(action_type:23)
  // on back-navigation to ConnectPmsStep.
  const setPmsHelpText = useCallback((data) => {
    dispatch({ type: CTX_ACTIONS.SET_PMS_HELP_TEXT, payload: data });
  }, []);

  const value = {
    // State
    ...state,
    // Actions
    setSession,
    setVerified,
    setCurrentStep,
    completeStep,
    setListings,
    setPmsHint,
    setQualificationData,
    setStripeClientSecret,
    setStripePaymentCaptured,
    setConnectedPms,
    setHasActivePms,
    setPmsList,
    setThemesList,
    setMessageTemplates,
    setTaskVerbiages,
    setPmsHelpText,
    startPmsPolling,
    stopPmsPolling,
    appendChat,
    sendChatMessage,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

/**
 * useOnboarding
 * Hook to access the OnboardingContext. Must be used inside OnboardingProvider.
 */
export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return ctx;
};

export default OnboardingContext;
